import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PrivacyPolicy() {
  return <div className="min-h-screen bg-gradient-to-br from-[#8554F6] to-[#C5ACFF] px-4 py-8" data-id="element-41">
      <div className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-md overflow-hidden" data-id="element-42">
        <div className="p-6 md:p-10" data-id="element-43">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#8B5CF6] hover:underline mb-6" data-id="element-44">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-45" />
            Back to Home
          </Link>

          <h1 className="text-3xl font-bold text-gray-800 mb-1" data-id="element-46">
            InterviewAce Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mb-8" data-id="element-47">Last updated July 2025</p>

          <div className="prose prose-gray max-w-none text-gray-700 text-[15px] leading-relaxed space-y-6" data-id="element-48">
            <p data-id="element-49">
              This privacy notice for Veer Technologies Limited
              (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;),
              describes how and why we might collect, store, use, and/or share
              (&ldquo;process&rdquo;) your information when you use our
              services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-1" data-id="element-50">
              <li data-id="element-51">
                Visit app.interviewace.ai, or any website of ours that links to
                this privacy notice
              </li>
              <li data-id="element-52">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p data-id="element-53">
              <strong data-id="element-54">Questions or concerns?</strong> Reading this privacy
              notice will help you understand your privacy rights and choices.
              If you do not agree with our policies and practices, please do not
              use our Services. If you still have any questions or concerns,
              please contact us at{' '}
              <a href="mailto:help@teamveer.com" className="text-[#8B5CF6] hover:underline" data-id="element-55">
                help@teamveer.com
              </a>
              .
            </p>

            <hr className="border-gray-200" data-id="element-56" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-57">
              SUMMARY OF KEY POINTS
            </h2>
            <p data-id="element-58">
              This summary provides key points from our privacy notice, but you
              can find out more details about any of these topics by clicking
              the link following each key point or by using our table of
              contents below to find the section you are looking for.
            </p>
            <p data-id="element-59">
              <strong data-id="element-60">What personal information do we process?</strong> When you
              visit, use, or navigate our Services, we process information about
              how you interact with us and our Services, the choices you make,
              and the products and features you use. This information we collect
              automatically from usage of our Services is anonymous, and used
              purely to understand how to improve them. We also collect
              information you disclose to us in communications with us.
            </p>
            <p data-id="element-61">
              <strong data-id="element-62">Do we process any sensitive personal information?</strong>{' '}
              No, we do not collect, nor process any sensitive personal
              information, as defined by relevant EU and UK laws.
            </p>
            <p data-id="element-63">
              <strong data-id="element-64">Do we receive any information from third parties?</strong>{' '}
              We receive participant IDs from our partner, Reed in Partnership,
              however this does not include any additional personal information
              (including names, etc). We also use third-parties as part of our
              Services to provide various functions including analytics.
            </p>
            <p data-id="element-65">
              <strong data-id="element-66">How do we process your information?</strong> We process
              your information to provide, improve, and administer our Services,
              communicate with you, for security and fraud prevention, and to
              comply with law. We may also process your information for other
              purposes with your consent. We process your information only when
              we have a valid legal reason to do so.
            </p>
            <p data-id="element-67">
              <strong data-id="element-68">
                In what situations and with which parties do we share personal
                information?
              </strong>{' '}
              We may share information in specific situations and with specific
              third parties. Learn more about when and with whom we share your
              personal information below under &lsquo;Third Party
              Services&rsquo;.
            </p>
            <p data-id="element-69">
              <strong data-id="element-70">How do we keep your information safe?</strong> We have
              organisational and technical processes and procedures in place to
              protect your personal information. However, no electronic
              transmission over the internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorised
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
            </p>
            <p data-id="element-71">
              <strong data-id="element-72">What are your rights?</strong> Depending on where you are
              located geographically, the applicable privacy law may mean you
              have certain rights regarding your personal information.
            </p>
            <p data-id="element-73">
              <strong data-id="element-74">How do you exercise your rights?</strong> The easiest way
              to exercise your rights is by contacting us at{' '}
              <a href="mailto:help@teamveer.com" className="text-[#8B5CF6] hover:underline" data-id="element-75">
                help@teamveer.com
              </a>
              . We will consider and act upon any request in accordance with
              applicable data protection laws.
            </p>

            <hr className="border-gray-200" data-id="element-76" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-77">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>

            <h3 className="text-lg font-semibold text-gray-800" data-id="element-78">
              Personal information you disclose to us
            </h3>
            <p data-id="element-79">
              <em data-id="element-80">
                In Short: We collect personal information that you provide to
                us.
              </em>
            </p>
            <p data-id="element-81">
              We collect personal information that you voluntarily provide to us
              when you express an interest in obtaining information about us or
              our products and Services, when you participate in activities on
              the Services, or otherwise when you contact us.
            </p>
            <p data-id="element-82">
              <strong data-id="element-83">Sensitive Information:</strong> We do not process
              sensitive information.
            </p>

            <h3 className="text-lg font-semibold text-gray-800" data-id="element-84">
              Information automatically collected
            </h3>
            <p data-id="element-85">
              <em data-id="element-86">
                In Short: Some information — such as your Internet Protocol (IP)
                address and/or browser and device characteristics — is collected
                automatically when you visit our Services.
              </em>
            </p>
            <p data-id="element-87">
              We automatically collect certain information when you visit, use,
              or navigate the Services. This information does not reveal your
              specific identity (like your name or contact information) but may
              include device and usage information, such as your IP address,
              browser and device characteristics, operating system, language
              preferences, referring URLs, device name, country, location,
              information about how and when you use our Services, and other
              technical information. This information is primarily needed to
              maintain the security and operation of our Services, and for our
              internal analytics and reporting purposes.
            </p>
            <p data-id="element-88">The information we collect includes:</p>
            <ul className="list-disc pl-6 space-y-1" data-id="element-89">
              <li data-id="element-90">
                <strong data-id="element-91">Log and Usage Data:</strong> Log and usage data is
                service-related, diagnostic, usage, and performance information
                our servers automatically collect when you access or use our
                Services and which we record in log files. Depending on how you
                interact with us, this log data may include your IP address,
                device information, browser type, and settings and information
                about your activity in the Services (such as the date/time
                stamps associated with your usage, pages and files viewed,
                searches, and other actions you take such as which features you
                use), device event information (such as system activity, error
                reports (sometimes called &lsquo;crash dumps&rsquo;), and
                hardware settings).
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800" data-id="element-92">
              Use of Cookies
            </h3>
            <p data-id="element-93">
              Like many websites, we use cookies to keep track of when you
              re-visit our Services. These cookies uniquely keep track of
              anonymous revisitations to our Services for our analytics and
              reporting purposes. In addition, we use third party libraries as
              described under Third Party Services below which may set cookies
              when you use our Services as well.
            </p>

            <h3 className="text-lg font-semibold text-gray-800" data-id="element-94">
              Microsoft Clarity
            </h3>
            <p data-id="element-95">
              We partner with Microsoft Clarity and Microsoft Advertising to
              capture how you use and interact with our website through
              behavioral metrics, heatmaps, and session replay to improve and
              market our products/services. Website usage data is captured using
              first and third-party cookies and other tracking technologies to
              determine the popularity of products/services and online activity.
              Additionally, we use this information for site optimization and
              security purposes. For more information about how Microsoft
              collects and uses your data, visit the{' '}
              <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline" data-id="element-96">
                Microsoft Privacy Statement
              </a>
              .
            </p>

            <hr className="border-gray-200" data-id="element-97" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-98">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <p data-id="element-99">
              <em data-id="element-100">
                In Short: We process your information to provide, improve, and
                administer our Services, communicate with you, for security and
                fraud prevention, and to comply with law. We may also process
                your information for other purposes with your consent.
              </em>
            </p>
            <p data-id="element-101">
              We process your personal information for a variety of reasons,
              depending on how you interact with our Services, including:
            </p>
            <ul className="list-disc pl-6 space-y-1" data-id="element-102">
              <li data-id="element-103">
                <strong data-id="element-104">
                  To deliver and facilitate delivery of services to the user.
                </strong>{' '}
                We may process your information to provide you with the
                requested service.
              </li>
              <li data-id="element-105">
                <strong data-id="element-106">
                  To save or protect an individual&rsquo;s vital interest.
                </strong>{' '}
                We may process your information when necessary to save or
                protect an individual&rsquo;s vital interest, such as to prevent
                harm.
              </li>
            </ul>

            <hr className="border-gray-200" data-id="element-107" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-108">
              3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
            </h2>
            <p data-id="element-109">
              <em data-id="element-110">
                In Short: We only process your personal information when we
                believe it is necessary and we have a valid legal reason (i.e.
                legal basis) to do so under applicable law, like with your
                consent, to comply with laws, to provide you with services to
                enter into or fulfil our contractual obligations, to protect
                your rights, or to fulfil our legitimate business interests.
              </em>
            </p>
            <p data-id="element-111">
              The General Data Protection Regulation (GDPR) and UK GDPR require
              us to explain the valid legal bases we rely on in order to process
              your personal information. As such, we may rely on the following
              legal bases to process your personal information:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-112">
              <li data-id="element-113">
                <strong data-id="element-114">Consent:</strong> We may process your information if you
                have given us permission (i.e. consent) to use your personal
                information for a specific purpose. You can withdraw your
                consent at any time.
              </li>
              <li data-id="element-115">
                <strong data-id="element-116">Performance of a Contract:</strong> We may process your
                personal information when we believe it is necessary to fulfil
                our contractual obligations to you, including providing our
                Services or at your request prior to entering into a contract
                with you.
              </li>
              <li data-id="element-117">
                <strong data-id="element-118">Legal obligations:</strong> We may process your
                information where we believe it is necessary for compliance with
                our legal obligations, such as to cooperate with a law
                enforcement body or regulatory agency, exercise or defend our
                legal rights, or disclose your information as evidence in
                litigation in which we are involved.
              </li>
              <li data-id="element-119">
                <strong data-id="element-120">Vital interests:</strong> We may process your
                information where we believe it is necessary to protect your
                vital interests or the vital interests of a third party, such as
                situations involving potential threats to the safety of any
                person.
              </li>
            </ul>

            <hr className="border-gray-200" data-id="element-121" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-122">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR INFORMATION?
            </h2>
            <p data-id="element-123">
              <em data-id="element-124">
                In Short: We may share information in specific situations
                described in this section and/or with the following third
                parties.
              </em>
            </p>
            <p data-id="element-125">
              We may need to share your personal information in the following
              situations:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-126">
              <li data-id="element-127">
                <strong data-id="element-128">Business Transfers:</strong> We may share or transfer
                your information in connection with, or during negotiations of,
                any merger, sale of company assets, financing, or acquisition of
                all or a portion of our business to another company.
              </li>
              <li data-id="element-129">
                <strong data-id="element-130">Affiliates:</strong> We may share your information with
                our affiliates, in which case we will require those affiliates
                to honour this privacy notice. Affiliates include our parent
                company and any subsidiaries, joint venture partners, or other
                companies that we control or that are under common control with
                us.
              </li>
              <li data-id="element-131">
                <strong data-id="element-132">Third Party services:</strong> Our services use third
                parties as described next.
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800" data-id="element-133">
              Third-Party Services
            </h3>
            <p data-id="element-134">
              Our Services also may rely on various third parties for analytics,
              site reliability, security, protection and other purposes.
              Information about your usage of our Services may be shared with
              these third parties when you interact with our Services. These
              include:
            </p>
            <ul className="list-disc pl-6 space-y-1" data-id="element-135">
              <li data-id="element-136">
                <strong data-id="element-137">Posthog</strong> — Observability
              </li>
              <li data-id="element-138">
                <strong data-id="element-139">Microsoft</strong> — Hosting, STT &amp; TTS (Voice)
              </li>
              <li data-id="element-140">
                <strong data-id="element-141">Clerk</strong> — User Authentication
              </li>
              <li data-id="element-142">
                <strong data-id="element-143">Twilio</strong> — User Verification &amp; Identity
              </li>
              <li data-id="element-144">
                <strong data-id="element-145">Livekit</strong> — Configuring Agentic Chatbots
              </li>
            </ul>
            <p data-id="element-146">
              A complete list of third-parties can be obtained by writing{' '}
              <a href="mailto:help@teamveer.com" className="text-[#8B5CF6] hover:underline" data-id="element-147">
                help@teamveer.com
              </a>
              .
            </p>

            <hr className="border-gray-200" data-id="element-148" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-149">
              5. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <p data-id="element-150">
              <em data-id="element-151">
                In Short: We keep your information for as long as necessary to
                fulfil the purposes outlined in this privacy notice unless
                otherwise required by law.
              </em>
            </p>
            <p data-id="element-152">
              We will only keep your personal information for as long as it is
              necessary for the purposes set out in this privacy notice, unless
              a longer retention period is required or permitted by law (such as
              tax, accounting, or other legal requirements).
            </p>
            <p data-id="element-153">
              When we have no ongoing legitimate business need to process your
              personal information, we will either delete or anonymise such
              information, or, if this is not possible (for example, because
              your personal information has been stored in backup archives),
              then we will securely store your personal information and isolate
              it from any further processing until deletion is possible.
            </p>

            <hr className="border-gray-200" data-id="element-154" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-155">
              6. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <p data-id="element-156">
              <em data-id="element-157">
                In Short: We aim to protect your personal information through a
                system of organisational and technical security measures.
              </em>
            </p>
            <p data-id="element-158">
              We have implemented appropriate and reasonable technical and
              organisational security measures designed to protect the security
              of any personal information we process. However, despite our
              safeguards and efforts to secure your information, no electronic
              transmission over the Internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorised
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
              Although we will do our best to protect your personal information,
              transmission of personal information to and from our Services is
              at your own risk. You should only access the Services within a
              secure environment.
            </p>

            <hr className="border-gray-200" data-id="element-159" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-160">
              7. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <p data-id="element-161">
              <em data-id="element-162">
                In Short: In many regions, such as the European Economic Area
                (EEA), United Kingdom (UK), and Switzerland, you have rights
                that allow you greater access to and control over your personal
                information. You may review, change, or terminate your account
                at any time.
              </em>
            </p>
            <p data-id="element-163">
              In some regions (like the EEA, UK, and Switzerland), you have
              certain rights under applicable data protection laws. These may
              include the right (i) to request access and obtain a copy of your
              personal information, (ii) to request rectification or erasure;
              (iii) to restrict the processing of your personal information;
              (iv) if applicable, to data portability; and (v) not to be subject
              to automated decision-making. In certain circumstances, you may
              also have the right to object to the processing of your personal
              information. You can make such a request by contacting us by using
              the contact details provided in the section &lsquo;HOW CAN YOU
              CONTACT US ABOUT THIS NOTICE?&rsquo; below.
            </p>
            <p data-id="element-164">
              We will consider and act upon any request in accordance with
              applicable data protection laws.
            </p>
            <p data-id="element-165">
              If you are located in the EEA or UK and you believe we are
              unlawfully processing your personal information, you also have the
              right to complain to your Member State data protection authority
              or UK data protection authority.
            </p>
            <p data-id="element-166">
              If you are located in Switzerland, you may contact the Federal
              Data Protection and Information Commissioner.
            </p>
            <p data-id="element-167">
              <strong data-id="element-168">Withdrawing your consent:</strong> If we are relying on
              your consent to process your personal information, you have the
              right to withdraw your consent at any time. You can withdraw your
              consent at any time by contacting us by using the contact details
              provided in the section &lsquo;HOW CAN YOU CONTACT US ABOUT THIS
              NOTICE?&rsquo; below.
            </p>
            <p data-id="element-169">
              However, please note that this will not affect the lawfulness of
              the processing before its withdrawal nor, will it affect the
              processing of your personal information conducted in reliance on
              lawful processing grounds other than consent.
            </p>

            <hr className="border-gray-200" data-id="element-170" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-171">
              8. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <p data-id="element-172">
              Most web browsers and some mobile operating systems and mobile
              applications include a Do-Not-Track (&ldquo;DNT&rdquo;) feature or
              setting you can activate to signal your privacy preference not to
              have data about your online browsing activities monitored and
              collected. At this stage no uniform technology standard for
              recognising and implementing DNT signals has been finalised.
            </p>
            <p data-id="element-173">
              As such, we do not currently respond to DNT browser signals or any
              other mechanism that automatically communicates your choice not to
              be tracked online. If a standard for online tracking is adopted
              that we must follow in the future, we will inform you about that
              practice in a revised version of this privacy notice.
            </p>

            <hr className="border-gray-200" data-id="element-174" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-175">
              9. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <p data-id="element-176">
              <em data-id="element-177">
                In Short: Yes, we will update this notice as necessary to stay
                compliant with relevant laws.
              </em>
            </p>
            <p data-id="element-178">
              We may update this privacy notice from time to time. The updated
              version will be indicated by an updated &lsquo;Revised&rsquo; date
              and the updated version will be effective as soon as it is
              accessible.
            </p>
            <p data-id="element-179">
              If we make material changes to this privacy notice, we may notify
              you either by prominently posting a notice of such changes or by
              directly sending you a notification. We encourage you to review
              this privacy notice frequently to be informed of how we are
              protecting your information.
            </p>

            <hr className="border-gray-200" data-id="element-180" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-181">
              10. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <p data-id="element-182">
              If you have questions or comments about this notice, you may
              contact our Data Protection Officer (DPO) by email at{' '}
              <a href="mailto:help@teamveer.com" className="text-[#8B5CF6] hover:underline" data-id="element-183">
                help@teamveer.com
              </a>
              , or contact us by post at:
            </p>
            <address className="not-italic bg-gray-50 rounded-md p-4 text-gray-700" data-id="element-184">
              Data Protection Officer
              <br data-id="element-185" />
              Veer Technologies Limited
              <br data-id="element-186" />
              Acre House
              <br data-id="element-187" />
              11-15 William Road
              <br data-id="element-188" />
              London NW1 3ER
            </address>

            <hr className="border-gray-200" data-id="element-189" />

            <h2 className="text-xl font-bold text-gray-800 mt-8" data-id="element-190">
              11. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </h2>
            <p data-id="element-191">
              You have the right to request access to the personal information
              we collect from you, change that information, or delete it. To
              request to review, update, or delete your personal information,
              please email us at{' '}
              <a href="mailto:help@teamveer.com" className="text-[#8B5CF6] hover:underline" data-id="element-192">
                help@teamveer.com
              </a>
              .
            </p>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-200" data-id="element-193">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#8B5CF6] hover:underline" data-id="element-194">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-195" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>;
}